import bettingOptions from "../../data/p2pBettingOptions.json";

export const CHALLENGE_STAKES = [500, 1000, 2000, 5000, 10000];
export const CHALLENGE_ODDS = ["1.5", "2", "2.5", "3"];
export const MIN_CHALLENGE_STAKE = 500;

export const resolveLeagueName = (fixtureOrLeague) => {
  const league = fixtureOrLeague?.league ?? fixtureOrLeague;
  if (typeof league === "string" && league.trim()) return league.trim();
  if (league?.name && String(league.name).trim()) return String(league.name).trim();
  return "";
};

export const maxCounterCapacity = (budget, odds) => {
  const b = Number(budget);
  const o = Number(odds);
  if (!Number.isFinite(b) || b <= 0 || !Number.isFinite(o) || o <= 1) return 0;
  return Math.round((b / (o - 1)) * 100) / 100;
};

/** @deprecated legacy slot count presets — use budget + odds instead. */
export const CHALLENGE_SLOTS = [1, 2, 3, 4, 5];

const SECTION_LABELS = {
  oneXTwo: "Match result",
  doubleChance: "Double chance",
  bothTeamsToScore: "Both teams to score",
  overUnderGoals: "Over / Under goals",
  correctScore: "Correct score",
  cleanSheet: "Clean sheet",
  oddEven: "Odd / Even goals",
  teamToScore: "Team to score",
  winToNil: "Win to nil",
  drawNoBet: "Draw no bet",
};

const subsectionLabel = (subSection, fixtureRef) => {
  if (subSection === "fullTime") return "Full time";
  if (subSection === "halfTime") return "First half";
  if (subSection === "secondHalf") return "Second half";
  if (subSection === "homeTeam") return fixtureRef?.homeTeam?.name || "Home team";
  if (subSection === "awayTeam") return fixtureRef?.awayTeam?.name || "Away team";
  return subSection;
};

const outcomeLabel = (section, key, fixtureRef) => {
  const home = fixtureRef?.homeTeam?.name || "Home";
  const away = fixtureRef?.awayTeam?.name || "Away";
  if (section === "oneXTwo") {
    if (key === "1") return home;
    if (key === "X") return "Draw";
    if (key === "2") return away;
  }
  if (section === "doubleChance") {
    if (key === "1X") return `${home} or Draw`;
    if (key === "X2") return `Draw or ${away}`;
    if (key === "12") return `${home} or ${away}`;
  }
  if (section === "overUnderGoals") {
    const [direction, line = ""] = String(key).split(/(?=\d)/);
    return `${direction === "over" ? "Over" : "Under"} ${line.replace("_", ".")}`;
  }
  if (section === "teamToScore" || section === "drawNoBet") {
    if (key === "homeTeam") return home;
    if (key === "awayTeam") return away;
    if (key === "bothTeams") return "Both teams";
    if (key === "noGoal") return "No goal";
  }
  if (key === "yes") return "Yes";
  if (key === "no") return "No";
  if (key === "odd") return "Odd";
  if (key === "even") return "Even";
  return String(key).replaceAll("_", ".");
};

export const buildChallengeMarkets = (fixtureRef) =>
  Object.entries(bettingOptions)
    .filter(([section]) => section !== "doubleChanceAndBothTeamsToScore")
    .flatMap(([section, sectionData]) =>
      Object.entries(sectionData)
        .filter(
          ([subSection, values]) =>
            Array.isArray(values) &&
            !(section === "teamToScore" && subSection === "lastThreeWay")
        )
        .map(([subSection, values]) => ({
          id: `${section}:${subSection}`,
          title: SECTION_LABELS[section] || section,
          subtitle: subsectionLabel(subSection, fixtureRef),
          outcomes: values.map((key) => ({
            id: `${section}:${subSection}:${key}`,
            label: outcomeLabel(section, key, fixtureRef),
            agreedOdds: { section, subSection, key },
          })),
        }))
    );

export const buildChallengePickOptions = (fixtureRef) => {
  const home = fixtureRef?.homeTeam?.name || "Home";
  const away = fixtureRef?.awayTeam?.name || "Away";
  return [
    { id: "1", group: "result", label: home, hint: "Home win", agreedOdds: { section: "oneXTwo", subSection: "fullTime", key: "1" } },
    { id: "X", group: "result", label: "Draw", hint: "Full time", agreedOdds: { section: "oneXTwo", subSection: "fullTime", key: "X" } },
    { id: "2", group: "result", label: away, hint: "Away win", agreedOdds: { section: "oneXTwo", subSection: "fullTime", key: "2" } },
    { id: "btts-y", group: "goals", label: "Both teams score", hint: "Yes", agreedOdds: { section: "bothTeamsToScore", subSection: "fullTime", key: "yes" } },
    { id: "btts-n", group: "goals", label: "Both teams score", hint: "No", agreedOdds: { section: "bothTeamsToScore", subSection: "fullTime", key: "no" } },
    { id: "o25", group: "goals", label: "Total goals", hint: "Over 2.5", agreedOdds: { section: "overUnderGoals", subSection: "fullTime", key: "over2_5" } },
    { id: "u25", group: "goals", label: "Total goals", hint: "Under 2.5", agreedOdds: { section: "overUnderGoals", subSection: "fullTime", key: "under2_5" } },
  ];
};

export const buildPickLabel = (fixtureRef, agreedOdds) => {
  const matchingMarket = buildChallengeMarkets(fixtureRef).find(
    (market) =>
      market.outcomes[0]?.agreedOdds?.section === agreedOdds?.section &&
      market.outcomes[0]?.agreedOdds?.subSection === agreedOdds?.subSection
  );
  const matchingOutcome = matchingMarket?.outcomes.find(
    (outcome) => outcome.agreedOdds.key === agreedOdds?.key
  );
  if (matchingMarket && matchingOutcome) {
    return `${matchingMarket.title} · ${matchingOutcome.label} (${matchingMarket.subtitle})`;
  }

  const home = fixtureRef?.homeTeam?.name || "Home";
  const away = fixtureRef?.awayTeam?.name || "Away";
  const { section, key } = agreedOdds || {};
  if (section === "oneXTwo") {
    if (key === "1") return `${home} to win`;
    if (key === "X") return "Draw";
    if (key === "2") return `${away} to win`;
  }
  if (section === "bothTeamsToScore") {
    return key === "yes" ? "Both teams to score — Yes" : "Both teams to score — No";
  }
  if (section === "overUnderGoals") {
    return key === "over2_5" ? "Over 2.5 goals" : "Under 2.5 goals";
  }
  return "Challenge pick";
};

export const formatCash = (n) => `CASH ${Number(n || 0).toLocaleString()}`;

export const resolveMinStake = (challenge) =>
  Number(challenge?.minStake ?? challenge?.amount) || MIN_CHALLENGE_STAKE;

export const resolveOfferedOdds = (challenge) => {
  const n = Number(challenge?.offeredOdds);
  return Number.isFinite(n) && n > 1 ? n : 2;
};

export const resolveCreatorBudget = (challenge) =>
  Number(
    challenge?.creatorBudget ??
      Number(challenge?.amount || MIN_CHALLENGE_STAKE) *
        Number(challenge?.maxPeopleAllowed || 1)
  );

export const resolveRemainingBudget = (challenge) =>
  Number(challenge?.remainingBudget ?? 0);

export const resolveRemainingCapacity = (challenge) => {
  const explicit =
    challenge?.remainingCounterCapacity ?? challenge?.remainingCapacity;
  if (explicit != null && explicit !== "") {
    const n = Number(explicit);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  const budget = resolveCreatorBudget(challenge);
  const remaining = resolveRemainingBudget(challenge);
  if (budget > 0) {
    const odds = resolveOfferedOdds(challenge);
    if (odds > 1 && remaining >= 0) {
      return Math.round((remaining / (odds - 1)) * 100) / 100;
    }
  }
  const max = Number(challenge?.maxPeopleAllowed || 0);
  const joined = Number(challenge?.joinedPeople || 0);
  const minStake = resolveMinStake(challenge);
  return Math.max(0, (max - joined) * minStake);
};

export const counterPayout = (stake, odds) =>
  Math.round(Number(stake) * Number(odds) * 100) / 100;

/** Resolve remaining open counter slots (legacy display metric). */
export const resolveOpenSlots = (challenge) => {
  if (challenge?.openSlots != null && challenge?.openSlots !== "") {
    const explicit = Number(challenge.openSlots);
    if (Number.isFinite(explicit)) {
      return Math.max(0, explicit);
    }
  }
  const minStake = resolveMinStake(challenge);
  const cap = resolveRemainingCapacity(challenge);
  if (cap < minStake) return 0;
  return Math.max(0, Math.floor(cap / minStake));
};

export const challengeIsOpen = (challenge) => {
  if (!challenge) return false;
  const status = String(challenge.status || "open").toLowerCase();
  if (status !== "open") return false;
  if (resolveRemainingCapacity(challenge) < resolveMinStake(challenge)) {
    return false;
  }
  const kickoffMs = (() => {
    const ref = challenge.fixtureRef;
    if (!ref) return null;
    const ts = Number(ref.timestamp);
    if (Number.isFinite(ts) && ts > 0) return ts > 1e12 ? ts : ts * 1000;
    if (ref.date) {
      const ms = new Date(ref.date).getTime();
      return Number.isFinite(ms) ? ms : null;
    }
    return null;
  })();
  if (kickoffMs != null) {
    const msUntilStart = kickoffMs - Date.now();
    if (msUntilStart <= 5 * 60 * 1000) return false;
  }
  return true;
};

export const challengeProgress = (challenge) => {
  const budget = resolveCreatorBudget(challenge);
  if (!budget) return 0;
  const remaining = resolveRemainingBudget(challenge);
  const matched = Math.max(0, budget - remaining);
  return Math.min(100, Math.round((matched / budget) * 100));
};

const FINISHED_STATUS_SHORTS = new Set([
  "FT",
  "AET",
  "PEN",
  "ABD",
  "AWD",
  "WO",
  "CANC",
]);
const STALE_UPCOMING_STATUS_SHORTS = new Set(["NS", "TBD", "PST"]);
const MATCH_DURATION_MS = 2.5 * 60 * 60 * 1000;
const AFTER_END_WINDOW_MS = 24 * 60 * 60 * 1000;
const STALE_UPCOMING_MS = 3 * 24 * 60 * 60 * 1000;

/** Upcoming/live always; finished only within 24h after approx. full time. */
export const isChallengeFixtureVisible = (fixtureRef, nowMs = Date.now()) => {
  if (!fixtureRef) return false;
  const short = String(fixtureRef?.status?.short || "").toUpperCase();
  const kickoffMs =
    Number(fixtureRef?.timestamp) > 0
      ? Number(fixtureRef.timestamp) * 1000
      : null;

  if (!FINISHED_STATUS_SHORTS.has(short)) {
    if (
      kickoffMs &&
      STALE_UPCOMING_STATUS_SHORTS.has(short) &&
      nowMs - kickoffMs > STALE_UPCOMING_MS
    ) {
      return false;
    }
    return true;
  }

  if (!kickoffMs) return false;
  const approxEndMs = kickoffMs + MATCH_DURATION_MS;
  return nowMs - approxEndMs <= AFTER_END_WINDOW_MS;
};

export const isVisibleChatChallengeMessage = (msg) =>
  !!(
    msg?.chatChallenge?.p2pBetId &&
    msg?.chatChallenge?.fixtureRef &&
    isChallengeFixtureVisible(msg.chatChallenge.fixtureRef)
  );

export const CHALLENGE_INVOLVEMENT_FILTERS = [
  { id: "all", label: "Everyone" },
  { id: "created", label: "Created" },
  { id: "joined", label: "Joined" },
];

const challengeCreatorId = (msg) =>
  String(msg?.userId?._id || msg?.userId || "");

export const isChatChallengeCreatedByMe = (msg, userId) => {
  if (!userId || !msg) return false;
  return challengeCreatorId(msg) === String(userId);
};

export const isChatChallengeJoinedByMe = (msg, userId) => {
  if (!userId || !msg?.chatChallenge) return false;
  if (isChatChallengeCreatedByMe(msg, userId)) return false;
  const uid = String(userId);
  const joinedIds = msg.chatChallenge.joinedUserIds || [];
  if (joinedIds.some((id) => String(id) === uid)) return true;
  return (msg.chatChallenge.recentCounters || []).some(
    (counter) => String(counter.userId) === uid
  );
};

export const matchesChallengeInvolvement = (msg, involvement, userId) => {
  if (!involvement || involvement === "all") return true;
  if (involvement === "created") return isChatChallengeCreatedByMe(msg, userId);
  if (involvement === "joined") return isChatChallengeJoinedByMe(msg, userId);
  return true;
};

